import apiClient, { ApiError, ApiParams } from '@/utility/apiClient'
import { handleApiError } from '@/lib/api/utils/errorHandler'
import { SESSION_STORAGE_KEYS } from '@/constants/storageKeys'
import { NotificationSettingsResponse, NotificationSettingsRequest } from '@/types/notification'
import {
  AuthResponse,
  SignupData,
  ProfileDetailsData,
  ProfileUpdateData,
  GoogleAuthData,
  TwitterAuthData,
  RegistrationData,
  InterestsData,
  NotificationStatusData,
  CategoryFollowsData,
  RoleModelFinderData,
  CareerInterviewData,
  ContentQueryParams,
  MemberQuitData,
  EmailResetData,
  PasswordChangeData,
  EducationBackgroundPostData,
} from '@/lib/api/types/members'
import type { SnsType } from '@/lib/api/types/members'

/**
 * Member API services
 */
export const MembersService = {
  /**
   * Get CSRF token
   */
  getCsrfToken: async (options?: { skipRequestCancellation?: boolean }): Promise<string> => {
    try {
      const url = 'members/get_csrf_token'

      const response = await apiClient.request('GET', url, {
        credentials: 'include', // Ensure cookies are sent
        skipRequestCancellation: !!options?.skipRequestCancellation,
      })

      // The response should have camelCase keys due to camelcaseKeys
      const token = response.data.csrfToken || response.data.csrf_token

      if (!token) {
        throw new Error('CSRF token not found in response')
      }

      return token
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get member profile
   */
  getMemberProfile: async (memberId: string): Promise<any> => {
    try {
      const url = `members/${memberId}/profile`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get current member profile
   */
  getCurrentProfile: async (): Promise<any> => {
    try {
      const url = 'members/profile'
      const response = await apiClient.request('GET', url, {
        credentials: 'include', // Ensure cookies are sent for session-based auth
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Update member profile
   */
  updateProfile: async (memberId: string, data: ProfileUpdateData): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()

      const url = `members/${memberId}/profile`
      const headers = { 'X-CSRF-Token': csrfToken }

      const response = await apiClient.request('PATCH', url, {
        data,
        headers,
        credentials: 'include', // Ensure cookies are sent for session-based auth
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Register a new member
   */
  signup: async (signupData: SignupData): Promise<AuthResponse> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/signup'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: signupData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Google auth signin/signup
   */
  googleAuth: async (authData: GoogleAuthData): Promise<AuthResponse> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/google_auth'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: authData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Twitter auth signin/signup
   */
  twitterAuth: async (authData: TwitterAuthData): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/twitter_auth'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: authData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Logout current member
   */
  logout: async (): Promise<void> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/logout'
      const headers = { 'X-CSRF-Token': csrfToken }
      await apiClient.request('PATCH', url, {
        headers,
        data: {},
      })

      // Clear client-side storage
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem('member')
      }
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Activate account with token
   */
  activateAccount: async (token: string): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/activate/${token}`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('GET', url, { headers })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * urlのactivate paramを検証してメールアドレスを取得
   */
  validateActivationToken: async (params: string): Promise<any> => {
    try {
      const url = `members/activate/${params}`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * アクティベーショントークンで会員登録を完了
   */
  completeRegistration: async (
    token: string,
    registrationData: RegistrationData,
  ): Promise<{ memberId: number }> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/activate/${token}`
      const headers = { 'X-CSRF-Token': csrfToken }

      const response = await apiClient.request('POST', url, {
        data: registrationData,
        headers,
      })

      return response.data
    } catch (error) {
      console.error('[API Debug] Registration error:', error)
      handleApiError(error)
    }
  },

  /**
   * Get member histories
   */
  getMemberHistories: async (
    memberId: string,
    limit: number = 30,
    offset: number = 0,
  ): Promise<any> => {
    try {
      const url = `members/${memberId}/histories`
      const params = { limit, offset }
      const response = await apiClient.request('GET', url, { params })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get member interests
   */
  getMemberInterests: async (memberId: string): Promise<any> => {
    try {
      const url = `members/${memberId}/interests`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Update member interests
   */
  updateMemberInterests: async (memberId: string, interestsData: InterestsData): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/interests`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('PATCH', url, {
        data: interestsData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get email notification settings
   */
  getEmailNotificationSettings: async (memberId: string): Promise<NotificationSettingsResponse> => {
    try {
      const url = `members/${memberId}/settings/email_notification`

      const response = await apiClient.request('GET', url, {
        credentials: 'include', // Ensure cookies are sent for session-based auth
      })

      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Update email notification settings
   */
  updateEmailNotificationSettings: async (
    memberId: string,
    settingsData: NotificationSettingsRequest,
  ): Promise<NotificationSettingsResponse> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()

      const url = `members/${memberId}/settings/email_notification`
      const headers = { 'X-CSRF-Token': csrfToken }

      const response = await apiClient.request('PATCH', url, {
        data: settingsData,
        headers,
      })

      return response.data
    } catch (error) {
      // メール通知設定更新エラー
      handleApiError(error)
    }
  },

  /**
   * Get member favorite stories
   */
  getMemberFavoriteStories: async (memberId: string, page: number = 1): Promise<any> => {
    try {
      const url = `members/${memberId}/favorite_story`
      const response = await apiClient.request('GET', url, { params: { page } })
      return response.data
    } catch (error) {
      return {
        records: [],
        pageMeta: {
          currentPage: 1,
          totalPages: 1,
        },
      }
    }
  },

  /**
   * Add favorite story
   */
  addFavoriteStory: async (memberId: string, storyId: string): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/favorite_story`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: { story_id: storyId },
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Remove favorite story
   */
  removeFavoriteStory: async (memberId: string, storyId: string): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/favorite_story`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('DELETE', url, {
        data: { story_id: storyId },
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get member notifications
   */
  getMemberNotifications: async (memberId: string): Promise<any> => {
    try {
      const url = `members/${memberId}/notification`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      // リクエストキャンセルは無視して空のレスポンスを返す
      if (error instanceof ApiError && error.statusText === 'Cancelled') {
        return {
          values: {
            notices: { title: '', data: [], readAt: null, confirmed: true },
            info: { title: '', data: [], readAt: null, confirmed: true },
          },
        }
      }
      handleApiError(error)
    }
  },

  /**
   * Update member notification read status
   */
  updateNotificationStatus: async (
    memberId: string,
    notificationData: NotificationStatusData,
  ): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/notification`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: notificationData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get quit (account deletion) questions
   */
  getQuitQuestions: async (): Promise<any> => {
    try {
      const url = 'members/quit_questions'
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Quit (delete) member account
   */
  quitMember: async (memberId: string, quitData: MemberQuitData): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/quit`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: quitData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Request email reset
   */
  requestEmailReset: async (emailData: EmailResetData): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/reset_email'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: emailData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Verify reset email token
   */
  verifyResetEmailToken: async (token: string): Promise<any> => {
    try {
      const url = `members/reset_email/${token}`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Reset email with token
   */
  resetEmail: async (token: string, emailData: { email: string }): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/reset_email/${token}`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('PATCH', url, {
        data: emailData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Request password reset
   */
  requestPasswordReset: async (email: string): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/reset_password'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: { email },
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Verify reset password token
   */
  verifyResetPasswordToken: async (token: string): Promise<any> => {
    try {
      const url = `members/reset_password/${token}`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Reset password with token
   */
  resetPassword: async (
    token: string,
    passwordData: {
      password?: string
      passwordConfirmation?: string
      email?: string
      new_password?: string
      new_password_confirmation?: string
    },
  ): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/password/${token}`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('PATCH', url, {
        data: passwordData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Change password
   */
  changePassword: async (memberId: string, passwordData: PasswordChangeData): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/change_password`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: passwordData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Check authentication status
   */
  checkAuth: async (): Promise<any> => {
    try {
      const url = 'members/auth_chk'
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get member mypage data
   */
  getMemberMypage: async (memberId: string): Promise<any> => {
    try {
      const url = `members/${memberId}/mypage`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get following categories content list
   */
  getFollowingCategoriesContentsList: async (memberId: string): Promise<any> => {
    try {
      const url = `members/${memberId}/mypage/following-categories/contents-list`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get following categories contents
   */
  getFollowingCategoriesContents: async (
    memberId: string,
    params: ContentQueryParams = {},
  ): Promise<any> => {
    try {
      const url = `members/${memberId}/mypage/following-categories/contents`
      const response = await apiClient.request('GET', url, { params: params as ApiParams })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get following companies content list
   */
  getFollowingCompaniesContentsList: async (memberId: string): Promise<any> => {
    try {
      const url = `members/${memberId}/mypage/following-companies/contents-list`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get following companies contents
   */
  getFollowingCompaniesContents: async (
    memberId: string,
    params: ContentQueryParams = {},
  ): Promise<any> => {
    try {
      const url = `members/${memberId}/mypage/following-companies/contents`
      const response = await apiClient.request('GET', url, { params: params as ApiParams })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get member category follows
   */
  getMemberCategoryFollows: async (memberId: string): Promise<any> => {
    try {
      const url = `members/${memberId}/category_follows`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Update member category follows
   */
  updateMemberCategoryFollows: async (
    memberId: string,
    followData: CategoryFollowsData,
  ): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/category_follows`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('PATCH', url, {
        data: followData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get member dashboard data
   */
  getMemberDashboard: async (memberId: string): Promise<any> => {
    try {
      const url = `members/${memberId}/dashboard`
      const response = await apiClient.request('GET', url, {
        credentials: 'include',
      })
      return response.data
    } catch (error) {
      // リクエストキャンセルは無視して空のレスポンスを返す
      if (error instanceof ApiError && error.statusText === 'Cancelled') {
        return {
          following: {
            companies: [],
            categories: [],
          },
          favoriteStoryIds: [],
          joinedGroups: [],
        }
      }
      handleApiError(error)
    }
  },

  /**
   * Get follow recommendations for member
   */
  getFollowRecommendations: async (
    memberId: string,
    params: ContentQueryParams = {},
  ): Promise<any> => {
    try {
      const url = `members/${memberId}/follow-recommend`
      const response = await apiClient.request('GET', url, { params: params as ApiParams })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get additional member info
   */
  getAdditionalInfo: async (): Promise<any> => {
    try {
      const url = 'members/additional_info'
      const response = await apiClient.request('GET', url, {
        credentials: 'include',
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Submit additional member info
   */
  submitAdditionalInfo: async (additionalData: ProfileDetailsData): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/additional_info'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: additionalData,
        headers,
        credentials: 'include', // Ensure cookies are sent for session-based auth
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get university and colleges list
   */
  getUniversityAndColleges: async (): Promise<any> => {
    try {
      const url = 'university_and_colleges'
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get faculties by university ID
   */
  getUniversityFaculties: async (universityId: number): Promise<any> => {
    try {
      const url = `university_and_colleges/${universityId}/faculties`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get departments by university ID and faculty ID
   */
  getFacultyDepartments: async (universityId: number, facultyId: number): Promise<any> => {
    try {
      const url = `university_and_colleges/${universityId}/faculties/${facultyId}/departments`
      const response = await apiClient.request('GET', url, { skipRequestCancellation: true })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get occupation groups list
   */
  getOccupationGroups: async (): Promise<any> => {
    try {
      const url = 'occupation_groups'
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get parent categories (industries) list
   */
  getParentCategories: async (): Promise<any> => {
    try {
      const url = 'parent_categories'
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Authenticate member
   * Uses /api/members/auth endpoint
   */
  auth: async (
    email: string,
    password: string,
    rememberMe: boolean = false,
  ): Promise<AuthResponse> => {
    try {
      let csrfToken: string | null = null

      try {
        csrfToken = await MembersService.getCsrfToken()
      } catch (csrfError) {
        console.error('Failed to get CSRF token:', csrfError)
      }

      const url = 'members/auth'
      const headers: Record<string, string> = {
        'X-Requested-With': 'XMLHttpRequest', // Rails expects this for AJAX requests
      }

      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken
      }

      const data: { email: string; password: string; remember_me?: string } = {
        email,
        password,
      }

      if (rememberMe) {
        data.remember_me = '1'
      }

      const requestOptions = {
        data,
        headers: Object.keys(headers).length > 0 ? headers : undefined,
        credentials: 'include' as const, // Ensure cookies are sent/received
      }

      const response = await apiClient.request('POST', url, requestOptions)

      // Check if the response has the expected structure
      // The API might return the member_id directly or in a nested object
      const responseData = response.data
      let memberId = responseData?.memberId || responseData?.member_id

      // Sometimes the response might be the memberId directly
      if (!memberId && typeof responseData === 'string') {
        memberId = responseData
      }

      // Or it might be in a nested structure
      if (!memberId && responseData && typeof responseData === 'object') {
        // Check for common API response patterns
        memberId =
          responseData.data?.memberId ||
          responseData.data?.member_id ||
          responseData.user?.id ||
          responseData.member?.id
      }

      if (!memberId) {
        throw new Error('Authentication failed: Invalid response from server')
      }

      // Store member data in session storage if available
      if (typeof window !== 'undefined') {
        const memberData = {
          id: memberId,
          email: email, // Include email for consistency with useAuth expectations
          // Add additional data from response if available
          ...(responseData.email && { email: responseData.email }),
          ...(responseData.name && { name: responseData.name }),
        }

        sessionStorage.setItem(SESSION_STORAGE_KEYS.MEMBER, JSON.stringify(memberData))
      }

      // Return standardized response
      return {
        memberId: String(memberId),
        ...responseData,
      }
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Create role model finder
   */
  createRoleModelFinder: async (
    memberId: string,
    roleModelFinderData: RoleModelFinderData,
  ): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken({ skipRequestCancellation: true })
      const url = `members/${memberId}/role_model_finder`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: roleModelFinderData,
        headers,
        skipRequestCancellation: true,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Create education background
   */
  createEducationBackground: async (educationData: EducationBackgroundPostData): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/profile/education_backgrounds'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: educationData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Update education background
   */
  updateEducationBackground: async (educationData: EducationBackgroundPostData): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/profile/education_backgrounds'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('PATCH', url, {
        data: educationData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get role model finders
   */
  getRoleModelFinders: async (memberId: string): Promise<any> => {
    try {
      const url = `members/${memberId}/role_model_finders`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Create career history
   */
  createCareerHistory: async (careerData: {
    work_experience_years: number
    industry_experiences: Array<{ category_id: number; experience_years: number }>
    occupation_experiences: Array<{ occupation_id: number; experience_years: number }>
  }): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/profile/career_histories'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: careerData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get role model finder details
   */
  getRoleModelFinderDetails: async (memberId: string, roleModelFinderId: string): Promise<any> => {
    try {
      const url = `members/${memberId}/role_model_finders/${roleModelFinderId}/role_model_finder_details`
      const response = await apiClient.request('GET', url)
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Update career history
   */
  updateCareerHistory: async (careerData: {
    work_experience_years: number
    industry_experiences: Array<{ category_id: number; experience_years: number }>
    occupation_experiences: Array<{ occupation_id: number; experience_years: number }>
  }): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/profile/career_histories'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('PATCH', url, {
        data: careerData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get career interview
   */
  getCareerInterview: async (memberId: string, params: ContentQueryParams = {}): Promise<any> => {
    try {
      const url = `members/${memberId}/member_interview_answers`
      const response = await apiClient.request('GET', url, {
        params: params as ApiParams,
        skipRequestCancellation: true,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Create career preferences
   */
  createCareerPreferences: async (preferenceData: {
    prefered_work_areas: number[]
    job_change_intent_level?: number
    interested_categories: Array<{ category_id: number }>
    interested_occupations: Array<{ occupation_id: number }>
  }): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/profile/career_preferences'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: preferenceData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Update career interview
   */
  updateCareerInterview: async (memberId: string, data: CareerInterviewData): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/member_interview_answers`
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('PATCH', url, {
        data: data,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Update career preferences
   */
  updateCareerPreferences: async (preferenceData: {
    prefered_work_areas: number[]
    job_change_intent_level?: number
    interested_categories: Array<{ category_id: number }>
    interested_occupations: Array<{ occupation_id: number }>
  }): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/profile/career_preferences'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('PATCH', url, {
        data: preferenceData,
        headers,
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Submit profile details (onboarding modal)
   */
  submitProfileDetails: async (profileData: ProfileDetailsData): Promise<any> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = 'members/profile_details'
      const headers = { 'X-CSRF-Token': csrfToken }
      const response = await apiClient.request('POST', url, {
        data: profileData,
        headers,
        credentials: 'include',
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Follow a company
   */
  followCompany: async (memberId: string, companyId: number): Promise<void> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/follows`
      const headers = { 'X-CSRF-Token': csrfToken }
      await apiClient.request('POST', url, {
        data: { company_id: companyId },
        headers,
        credentials: 'include',
      })
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Unfollow a company
   */
  unfollowCompany: async (memberId: string, companyId: number): Promise<void> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/follows`
      const headers = { 'X-CSRF-Token': csrfToken }
      await apiClient.request('DELETE', url, {
        data: { company_id: companyId },
        headers,
        credentials: 'include',
      })
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Follow a category
   */
  followCategory: async (memberId: string, categoryId: number): Promise<void> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/category_follows`
      const headers = { 'X-CSRF-Token': csrfToken }
      await apiClient.request('POST', url, {
        data: { category_id: categoryId },
        headers,
        credentials: 'include',
      })
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Unfollow a category
   */
  unfollowCategory: async (memberId: string, categoryId: number): Promise<void> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/category_follows`
      const headers = { 'X-CSRF-Token': csrfToken }
      await apiClient.request('DELETE', url, {
        data: { category_id: categoryId },
        headers,
        credentials: 'include',
      })
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Bulk follow companies
   */
  bulkFollowCompanies: async (memberId: string, companyIds: number[]): Promise<void> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/follows/bulk`
      const headers = { 'X-CSRF-Token': csrfToken }
      await apiClient.request('POST', url, {
        data: { company_ids: companyIds },
        headers,
        credentials: 'include',
      })
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Bulk add favorite stories
   */
  bulkAddFavoriteStories: async (memberId: string, storyIds: number[]): Promise<void> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/favorite_stories/bulk`
      const headers = { 'X-CSRF-Token': csrfToken }
      await apiClient.request('POST', url, {
        data: { story_ids: storyIds },
        headers,
        credentials: 'include',
      })
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Get member SNS list
   */
  getMemberSns: async (memberId: string): Promise<any> => {
    try {
      const url = `members/${memberId}/sns`
      const response = await apiClient.request('GET', url, {
        credentials: 'include',
      })
      return response.data
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Create member SNS
   * Sends member_id and provider_uid to Rails API to create MemberSns
   */
  createMemberSns: async (
    memberId: number,
    providerUid: string,
    snsType: SnsType,
  ): Promise<void> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/sns`
      const headers = { 'X-CSRF-Token': csrfToken }
      await apiClient.request('POST', url, {
        data: {
          provider_uid: providerUid,
          sns_type: snsType,
        },
        headers,
        credentials: 'include',
      })
    } catch (error) {
      handleApiError(error)
    }
  },

  /**
   * Delete member SNS
   */
  deleteMemberSns: async (memberId: string, snsType: SnsType): Promise<void> => {
    try {
      const csrfToken = await MembersService.getCsrfToken()
      const url = `members/${memberId}/sns`
      const headers = { 'X-CSRF-Token': csrfToken }
      await apiClient.request('DELETE', url, {
        data: { sns_type: snsType },
        headers,
        credentials: 'include',
      })
    } catch (error) {
      handleApiError(error)
    }
  },
}

// Legacy lowercase export for compatibility
export const membersService = MembersService
